/**
 * VersichertenPicker
 * Step 2 of the appointment wizard — search existing Versicherte or create a new one.
 */
import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search, Plus, ArrowLeft, ArrowRight, User, Check } from "lucide-react";
import {
  BirthdayPicker,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Skeleton,
} from "@jourfx/ui";

// ─── Types ────────────────────────────────────────────────────────────────────

interface VersichertenPickerProps {
  selectedVersicherteId: string | null;
  onSelect: (versicherteId: string) => void;
  onBack: () => void;
  onNext: () => void;
}

// ─── Create-mode schema ──────────────────────────────────────────────────────

const createVersichertenprofil = z.object({
  anrede: z.string().optional(),
  firstName: z.string().min(2, "Vorname muss mindestens 2 Zeichen lang sein"),
  lastName: z.string().min(2, "Nachname muss mindestens 2 Zeichen lang sein"),
  email: z
    .string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: "Bitte geben Sie eine gueltige E-Mail-Adresse ein",
    }),
  phone: z.string().optional(),
  versichertennummer: z.string().optional(),
  dateOfBirth: z.string().optional(),
  notes: z.string().optional(),
});

type CreateVersichertenFormData = z.infer<typeof createVersichertenprofil>;

// ─── Component ───────────────────────────────────────────────────────────────

export function VersichertenPicker({
  selectedVersicherteId,
  onSelect,
  onBack,
  onNext,
}: VersichertenPickerProps) {
  const [mode, setMode] = useState<"search" | "create">("search");
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const versicherte = useQuery(api.versichertenProfiles.index.list, {
    search: searchTerm || undefined,
  });

  const createVersichertenProfile = useMutation(api.versichertenProfiles.index.create);

  const form = useForm<CreateVersichertenFormData>({
    resolver: zodResolver(createVersichertenprofil),
    defaultValues: {
      anrede: "Herr",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      versichertennummer: "",
      dateOfBirth: "",
      notes: "",
    },
  });

  const handleCreate = form.handleSubmit(async (data) => {
    setIsCreating(true);
    try {
      const result = await createVersichertenProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        anrede: data.anrede || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        versichertennummer: data.versichertennummer || undefined,
        dateOfBirth: data.dateOfBirth || undefined,
        notes: data.notes || undefined,
      });
      onSelect(result.versichertenProfileId as string);
      form.reset();
      setMode("search");
    } catch (err) {
      console.error("Failed to create versicherten profile:", err);
    } finally {
      setIsCreating(false);
    }
  });

  // ─── Search mode ────────────────────────────────────────────────────────

  if (mode === "search") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Versicherten auswählen</CardTitle>
          <CardDescription>
            Suchen Sie einen bestehenden Versicherten oder legen Sie einen neuen an.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Name, E-Mail, Telefon oder Versichertennummer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Results */}
          <div className="max-h-72 overflow-y-auto rounded-md border">
            {versicherte === undefined ? (
              <div className="space-y-2 p-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 rounded-md" />
                ))}
              </div>
            ) : versicherte.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground">
                <User className="h-8 w-8 opacity-40" />
                <p>Keine Versicherten gefunden.</p>
              </div>
            ) : (
              <div className="divide-y">
                {versicherte.map((versicherter: any) => {
                  const isSelected =
                    selectedVersicherteId === (versicherter._id as string);
                  return (
                    <button
                      key={versicherter._id as string}
                      type="button"
                      onClick={() => onSelect(versicherter._id as string)}
                      className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-accent ${
                        isSelected
                          ? "ring-2 ring-primary ring-inset bg-primary/5"
                          : ""
                      }`}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                        {isSelected ? (
                          <Check className="h-4 w-4 text-primary" />
                        ) : (
                          <User className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {versicherter.firstName} {versicherter.lastName}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {[versicherter.email, versicherter.versichertennummer]
                            .filter(Boolean)
                            .join(" · ") || "Keine weiteren Angaben"}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Create new button */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setMode("create")}
          >
            <Plus className="mr-2 h-4 w-4" />
            Neuen Versicherten anlegen
          </Button>

          {/* Navigation */}
          <div className="flex justify-between pt-2">
            <Button type="button" variant="outline" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück
            </Button>
            <Button
              type="button"
              onClick={onNext}
              disabled={!selectedVersicherteId}
            >
              Weiter
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Create mode ────────────────────────────────────────────────────────

  return (
    <Card>
      <CardHeader>
        <CardTitle>Neuen Versicherten anlegen</CardTitle>
        <CardDescription>
          Erfassen Sie die Daten des neuen Versicherten.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleCreate} className="space-y-4">
          {/* Anrede */}
          <div className="space-y-2">
            <Label>Anrede</Label>
            <select
              {...form.register("anrede")}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="Herr">Herr</option>
              <option value="Frau">Frau</option>
              <option value="Divers">Divers</option>
              <option value="Dr.">Dr.</option>
              <option value="Prof.">Prof.</option>
            </select>
          </div>

          {/* Name */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="new-versicherte-first">Vorname *</Label>
              <Input
                id="new-versicherte-first"
                {...form.register("firstName")}
              />
              {form.formState.errors.firstName && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.firstName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-versicherte-last">Nachname *</Label>
              <Input
                id="new-versicherte-last"
                {...form.register("lastName")}
              />
              {form.formState.errors.lastName && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="new-versicherte-email">E-Mail</Label>
            <Input
              id="new-versicherte-email"
              type="email"
              {...form.register("email")}
            />
            {form.formState.errors.email && (
              <p className="text-sm text-destructive">
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="new-versicherte-phone">Telefon</Label>
            <Input
              id="new-versicherte-phone"
              type="tel"
              {...form.register("phone")}
            />
          </div>

          {/* Versichertennummer */}
          <div className="space-y-2">
            <Label htmlFor="new-versicherte-vnr">Versichertennummer</Label>
            <Input
              id="new-versicherte-vnr"
              {...form.register("versichertennummer")}
            />
          </div>

          {/* Date of birth */}
          <div className="space-y-2">
            <Label htmlFor="new-versicherte-dob">Geburtsdatum</Label>
            <BirthdayPicker
              id="new-versicherte-dob"
              value={form.watch("dateOfBirth") ?? ""}
              onChange={(date) => form.setValue("dateOfBirth", date)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="new-versicherte-notes">Notizen</Label>
            <textarea
              id="new-versicherte-notes"
              {...form.register("notes")}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={() => {
                form.reset();
                setMode("search");
              }}
              className="text-sm text-muted-foreground underline hover:text-foreground"
            >
              Zurück zur Suche
            </button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Versicherten anlegen
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
